import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  Phone, CreditCard, CheckCircle, XCircle, Loader2, RefreshCw, Trash2, Code, Eye, EyeOff,
  Repeat, Search,
} from 'lucide-react';
import axios from 'axios';
import DomiciliacionManager from './DomiciliacionManager';
import DIAttemptsAudit from './DIAttemptsAudit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAURA = {
  cuenta: '01380010320100324207',
  rif: 'J000000408320126',
  bank_code: '0138',
  bank_name: 'Banco Plaza',
};

// Cobrador específico de DOMICILIACIÓN (NO la cuenta operativa). El recurso del
// banco está autorizado para esta cuenta cobradora; la operativa devuelve A003.
const DOM_COBRADOR = {
  rif: 'J00408320126',
  cuenta: '01380010310100301339',
};

function todayYYYYMMDD() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDate(iso) {
  if (!iso) return 'N/A';
  try {
    return new Date(iso).toLocaleString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function StatusBadge({ success, code }) {
  const Icon = success ? CheckCircle : XCircle;
  const color = success ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full ${color}`}>
      <Icon className="w-3 h-3" /> {success ? 'OK' : (code || 'FAIL')}
    </span>
  );
}

function ResultPanel({ result, onClear }) {
  const [showRaw, setShowRaw] = useState(false);
  if (!result) return null;
  const { ok, summary = {}, raw = {} } = result;
  return (
    <div className="mt-5 border rounded-lg overflow-hidden" data-testid="sandbox-result-panel">
      <div className={`px-4 py-3 flex items-center justify-between ${ok ? 'bg-green-50 border-b border-green-200' : 'bg-red-50 border-b border-red-200'}`}>
        <div className="flex items-center gap-2">
          {ok ? <CheckCircle className="w-5 h-5 text-green-700" /> : <XCircle className="w-5 h-5 text-red-700" />}
          <div>
            <div className={`text-sm font-semibold ${ok ? 'text-green-800' : 'text-red-800'}`}>
              {ok ? 'Operación exitosa' : 'Operación fallida'}
            </div>
            <div className="text-xs text-slate-600">{summary.mensaje || summary.error || summary.descripcion || ''}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowRaw(s => !s)}
            data-testid="toggle-raw-view"
            className="text-xs px-2 py-1 bg-white border rounded hover:bg-slate-50 inline-flex items-center gap-1"
          >
            {showRaw ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {showRaw ? 'Vista compacta' : 'Vista técnica (JSON)'}
          </button>
          <button type="button" onClick={onClear} className="text-xs px-2 py-1 text-slate-500 hover:text-slate-800">Cerrar</button>
        </div>
      </div>

      {!showRaw ? (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {Object.entries(summary).map(([k, v]) => (
            <div key={k} className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wide text-slate-500">{k}</span>
              <span className="font-mono break-all">{v === null || v === undefined || v === '' ? '—' : String(v)}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-4 space-y-3 text-xs">
          <div>
            <div className="font-semibold mb-1 text-slate-600 flex items-center gap-1"><Code className="w-3 h-3" /> Request enviado al banco</div>
            <pre className="bg-slate-900 text-green-300 p-3 rounded overflow-x-auto" data-testid="raw-request-json">
{JSON.stringify(raw.request || {}, null, 2)}
            </pre>
          </div>
          <div>
            <div className="font-semibold mb-1 text-slate-600 flex items-center gap-1"><Code className="w-3 h-3" /> Response del banco</div>
            <pre className="bg-slate-900 text-blue-300 p-3 rounded overflow-x-auto" data-testid="raw-response-json">
{JSON.stringify(raw.response || {}, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}


function P2PSandbox({ setToast, onTested }) {
  const [form, setForm] = useState({
    phone_code: '0414',
    phone: '',
    reference: '',
    bank_code: '0102',
    amount: '',
    doc_type: 'V',
    doc_number: '',
    date: todayYYYYMMDD(),
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/sandbox/p2p/verify`,
        { ...form, amount: parseFloat(form.amount) },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(res.data);
      onTested?.();
      setToast?.({ type: res.data.ok ? 'success' : 'warning', message: res.data.ok ? 'Pago verificado correctamente' : (res.data.summary?.error || 'No se pudo verificar el pago') });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.error || JSON.stringify(detail)) : (detail || err.message);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="sandbox-p2p">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 mb-4">
        <strong>Cuenta receptora (Caura):</strong> {CAURA.cuenta} · {CAURA.bank_name} · RIF {CAURA.rif}
        <br />Realiza el Pago Móvil desde tu app bancaria a este teléfono/RIF y luego rellena los datos abajo para que el banco confirme el pago.
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500">Teléfono pagador (cód + número)</label>
            <div className="flex gap-2">
              <select value={form.phone_code} onChange={update('phone_code')} className="px-2 py-1.5 border rounded text-sm w-24" data-testid="p2p-phone-code">
                {['0412', '0414', '0416', '0422', '0424', '0426'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <input value={form.phone} onChange={update('phone')} className="flex-1 px-3 py-1.5 border rounded text-sm font-mono" placeholder="1234567" maxLength={7} required data-testid="p2p-phone" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Banco emisor (4 dígitos)</label>
            <input value={form.bank_code} onChange={update('bank_code')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={4} required data-testid="p2p-bank-code" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Tipo doc.</label>
            <select value={form.doc_type} onChange={update('doc_type')} className="w-full px-3 py-1.5 border rounded text-sm" data-testid="p2p-doc-type">
              <option value="V">V</option>
              <option value="E">E</option>
              <option value="J">J</option>
              <option value="G">G</option>
              <option value="P">P</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Cédula / RIF pagador</label>
            <input value={form.doc_number} onChange={update('doc_number')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="12345678" required data-testid="p2p-doc-number" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Referencia bancaria</label>
            <input value={form.reference} onChange={update('reference')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="123456" required data-testid="p2p-reference" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Monto (VES)</label>
            <input type="number" step="0.01" min="0.01" value={form.amount} onChange={update('amount')} className="w-full px-3 py-1.5 border rounded text-sm font-mono font-bold" placeholder="1.00" required data-testid="p2p-amount" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Fecha del pago (YYYYMMDD)</label>
            <input value={form.date} onChange={update('date')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={8} required data-testid="p2p-date" />
          </div>
        </div>
        <div className="flex justify-end pt-2 border-t">
          <button
            type="submit"
            disabled={busy}
            data-testid="p2p-verify-submit"
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
            {busy ? 'Verificando…' : 'Verificar pago P2P'}
          </button>
        </div>
      </form>

      <ResultPanel result={result} onClear={() => setResult(null)} />
    </div>
  );
}


function DebitoInmediatoSandbox({ setToast, onTested }) {
  const [form, setForm] = useState({
    cuenta_pagador: '',
    telefono_pagador: '',
    cod_banco_p: '0138',
    identificacion_p: '',
    nombre_p: '',
    monto: '',
    concepto: 'Sandbox DI - Caura Pay',
  });
  const [token, setToken] = useState('');
  const [busyToken, setBusyToken] = useState(false);
  const [busyCobro, setBusyCobro] = useState(false);
  const [tokenResult, setTokenResult] = useState(null);
  const [cobroResult, setCobroResult] = useState(null);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const requestToken = async (e) => {
    e.preventDefault();
    setBusyToken(true);
    setTokenResult(null);
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/sandbox/debito-inmediato/request-token`,
        {
          cuenta_pagador: form.cuenta_pagador,
          telefono_pagador: form.telefono_pagador,
          cod_banco_p: form.cod_banco_p,
          identificacion_p: form.identificacion_p,
          monto: parseFloat(form.monto),
        },
        { headers: { Authorization: `Bearer ${tk}` } },
      );
      setTokenResult(res.data);
      onTested?.();
      setToast?.({ type: res.data.ok ? 'success' : 'warning', message: res.data.summary?.mensaje || 'Sin respuesta del banco' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.error || JSON.stringify(detail)) : (detail || err.message);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setBusyToken(false);
    }
  };

  const executeCobro = async (e) => {
    e.preventDefault();
    if (!token.trim()) {
      setToast?.({ type: 'error', message: 'Ingresa el token recibido por SMS' });
      return;
    }
    setBusyCobro(true);
    setCobroResult(null);
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/sandbox/debito-inmediato/execute`,
        {
          token: token.trim(),
          cuenta_pagador: form.cuenta_pagador,
          telefono_pagador: form.telefono_pagador,
          cod_banco_p: form.cod_banco_p,
          identificacion_p: form.identificacion_p,
          nombre_p: form.nombre_p,
          monto: parseFloat(form.monto),
          concepto: form.concepto,
        },
        { headers: { Authorization: `Bearer ${tk}` } },
      );
      setCobroResult(res.data);
      onTested?.();
      setToast?.({ type: res.data.ok ? 'success' : 'warning', message: res.data.summary?.mensaje || 'Sin respuesta del banco' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.error || JSON.stringify(detail)) : (detail || err.message);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setBusyCobro(false);
    }
  };

  return (
    <div data-testid="sandbox-debito-inmediato">
      <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 mb-4">
        <strong>Cuenta cobradora (Caura):</strong> {CAURA.cuenta} · {CAURA.bank_name} · RIF {CAURA.rif}
        <br />El flujo es de dos pasos: (1) Solicitar token SMS al pagador y (2) Ejecutar el cobro con ese token.
      </div>

      <form onSubmit={requestToken} className="space-y-4">
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Paso 1 · Datos del pagador y monto</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500">Cuenta pagador (20 dígitos)</label>
            <input value={form.cuenta_pagador} onChange={update('cuenta_pagador')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={20} placeholder="01380002210025041486" required data-testid="di-cuenta-pagador" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Banco pagador (4 dígitos)</label>
            <input value={form.cod_banco_p} onChange={update('cod_banco_p')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={4} required data-testid="di-bank-code" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Teléfono pagador</label>
            <input value={form.telefono_pagador} onChange={update('telefono_pagador')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="04142899874" required data-testid="di-telefono" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Cédula / RIF pagador</label>
            <input value={form.identificacion_p} onChange={update('identificacion_p')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="V12345678" required data-testid="di-identificacion" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Nombre pagador</label>
            <input value={form.nombre_p} onChange={update('nombre_p')} className="w-full px-3 py-1.5 border rounded text-sm" placeholder="Pagador Prueba" required data-testid="di-nombre" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Monto (VES)</label>
            <input type="number" step="0.01" min="0.01" value={form.monto} onChange={update('monto')} className="w-full px-3 py-1.5 border rounded text-sm font-mono font-bold" placeholder="1.00" required data-testid="di-monto" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] text-slate-500">Concepto</label>
            <input value={form.concepto} onChange={update('concepto')} className="w-full px-3 py-1.5 border rounded text-sm" maxLength={80} data-testid="di-concepto" />
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={busyToken} data-testid="di-request-token-submit" className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 inline-flex items-center gap-2">
            {busyToken ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
            {busyToken ? 'Solicitando…' : 'Solicitar token (SMS)'}
          </button>
        </div>
      </form>

      <ResultPanel result={tokenResult} onClear={() => setTokenResult(null)} />

      <div className="border-t mt-6 pt-4">
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Paso 2 · Ejecutar cobro con token</h4>
        <form onSubmit={executeCobro} className="flex flex-col md:flex-row gap-3">
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Token recibido por SMS"
            className="flex-1 px-3 py-2 border rounded text-sm font-mono"
            data-testid="di-token-input"
            required
          />
          <button
            type="submit"
            disabled={busyCobro}
            data-testid="di-execute-submit"
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busyCobro ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {busyCobro ? 'Ejecutando…' : 'Ejecutar cobro'}
          </button>
        </form>
      </div>

      <ResultPanel result={cobroResult} onClear={() => setCobroResult(null)} />
    </div>
  );
}


function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function DomiciliacionSandbox({ setToast, onTested }) {
  const [form, setForm] = useState({
    id_cobrador: DOM_COBRADOR.rif,
    cuenta_cobrador: DOM_COBRADOR.cuenta,
    contrato: '',
    fecha_contrato: todayISO(),
    identificacion_d: '',
    cuenta_deudor: '',
    nombre_d: '',
    monto: '',
    concepto: 'Sandbox Domiciliacion - Caura Pay',
  });
  const [busyCobro, setBusyCobro] = useState(false);
  const [busyConsulta, setBusyConsulta] = useState(false);
  const [cobroResult, setCobroResult] = useState(null);
  const [consultaResult, setConsultaResult] = useState(null);
  const [transaccionId, setTransaccionId] = useState('');
  const [config, setConfig] = useState(null);

  const update = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    (async () => {
      try {
        const tk = localStorage.getItem('token');
        const res = await axios.get(`${BACKEND_URL}/api/super-admin/sandbox/domiciliacion/config`, { headers: { Authorization: `Bearer ${tk}` } });
        setConfig(res.data);
      } catch { /* no-op */ }
    })();
  }, []);

  const submitCobro = async (e) => {
    e.preventDefault();
    setBusyCobro(true);
    setCobroResult(null);
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/sandbox/domiciliacion/cobro`,
        { ...form, monto: parseFloat(form.monto) },
        { headers: { Authorization: `Bearer ${tk}` } },
      );
      setCobroResult(res.data);
      const tid = res.data?.summary?.transaccionId;
      if (tid) setTransaccionId(tid);
      onTested?.();
      setToast?.({ type: res.data.ok ? 'success' : (res.data.pendiente ? 'warning' : 'warning'), message: res.data.summary?.mensaje || 'Sin respuesta del banco' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.error || JSON.stringify(detail)) : (detail || err.message);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setBusyCobro(false);
    }
  };

  const submitConsulta = async (e) => {
    e.preventDefault();
    if (!transaccionId.trim()) {
      setToast?.({ type: 'error', message: 'Ingresa el transaccionId a consultar' });
      return;
    }
    setBusyConsulta(true);
    setConsultaResult(null);
    try {
      const tk = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/sandbox/domiciliacion/consulta`,
        { transaccionId: transaccionId.trim(), id_cobrador: form.id_cobrador, cuenta_cobrador: form.cuenta_cobrador },
        { headers: { Authorization: `Bearer ${tk}` } },
      );
      setConsultaResult(res.data);
      onTested?.();
      setToast?.({ type: res.data.ok ? 'success' : 'warning', message: res.data.summary?.mensaje || 'Sin respuesta del banco' });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.error || JSON.stringify(detail)) : (detail || err.message);
      setToast?.({ type: 'error', message: msg });
    } finally {
      setBusyConsulta(false);
    }
  };

  return (
    <div data-testid="sandbox-domiciliacion">
      <div className="rounded-lg bg-purple-50 border border-purple-200 p-3 text-xs text-purple-900 mb-4">
        <strong>Cobrador (Caura):</strong> {form.cuenta_cobrador} · RIF {form.id_cobrador}
        <br />La domiciliación cobra directo contra un <strong>contrato/mandato ya activo</strong> del deudor (sin token SMS). Flujo: (1) Cobro → (2) Consultar estatus si queda <code>PE</code> (pendiente por liquidación BCV).
        {config && (
          <div className="mt-2 pt-2 border-t border-purple-200 font-mono text-[10px] text-purple-700">
            Ambiente banco: <strong>{config.environment}</strong> · {config.base_url} · credenciales: {config.credentials_configured ? 'OK' : 'NO CONFIGURADAS'}
          </div>
        )}
      </div>

      <form onSubmit={submitCobro} className="space-y-4">
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Paso 1 · Datos del cobro</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-slate-500">RIF cobrador (Id)</label>
            <input value={form.id_cobrador} onChange={update('id_cobrador')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" data-testid="dom-id-cobrador" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Cuenta cobrador (20 dígitos)</label>
            <input value={form.cuenta_cobrador} onChange={update('cuenta_cobrador')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={20} data-testid="dom-cuenta-cobrador" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Contrato</label>
            <input value={form.contrato} onChange={update('contrato')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="202411151239" maxLength={15} required data-testid="dom-contrato" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Fecha contrato (YYYY-MM-DD)</label>
            <input value={form.fecha_contrato} onChange={update('fecha_contrato')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="2024-11-28" required data-testid="dom-fecha-contrato" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">RIF/Cédula deudor</label>
            <input value={form.identificacion_d} onChange={update('identificacion_d')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" placeholder="V13759368" required data-testid="dom-identificacion-d" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Cuenta deudor (20 dígitos)</label>
            <input value={form.cuenta_deudor} onChange={update('cuenta_deudor')} className="w-full px-3 py-1.5 border rounded text-sm font-mono" maxLength={20} placeholder="01340011130110248643" required data-testid="dom-cuenta-deudor" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Nombre deudor</label>
            <input value={form.nombre_d} onChange={update('nombre_d')} className="w-full px-3 py-1.5 border rounded text-sm" maxLength={80} placeholder="Doris Wells" required data-testid="dom-nombre-d" />
          </div>
          <div>
            <label className="text-[11px] text-slate-500">Monto (VES)</label>
            <input type="number" step="0.01" min="0.01" value={form.monto} onChange={update('monto')} className="w-full px-3 py-1.5 border rounded text-sm font-mono font-bold" placeholder="1.00" required data-testid="dom-monto" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] text-slate-500">Concepto</label>
            <input value={form.concepto} onChange={update('concepto')} className="w-full px-3 py-1.5 border rounded text-sm" maxLength={60} data-testid="dom-concepto" />
          </div>
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={busyCobro} data-testid="dom-cobro-submit" className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 inline-flex items-center gap-2">
            {busyCobro ? <Loader2 className="w-4 h-4 animate-spin" /> : <Repeat className="w-4 h-4" />}
            {busyCobro ? 'Ejecutando…' : 'Ejecutar cobro'}
          </button>
        </div>
      </form>

      <ResultPanel result={cobroResult} onClear={() => setCobroResult(null)} />

      <div className="border-t mt-6 pt-4">
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wide mb-3">Paso 2 · Consultar estatus (transaccionId)</h4>
        <form onSubmit={submitConsulta} className="flex flex-col md:flex-row gap-3">
          <input
            value={transaccionId}
            onChange={(e) => setTransaccionId(e.target.value)}
            placeholder="transaccionId devuelto por el cobro"
            className="flex-1 px-3 py-2 border rounded text-sm font-mono"
            data-testid="dom-transaccion-id"
            required
          />
          <button
            type="submit"
            disabled={busyConsulta}
            data-testid="dom-consulta-submit"
            className="px-4 py-2 text-sm bg-slate-700 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busyConsulta ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            {busyConsulta ? 'Consultando…' : 'Consultar estatus'}
          </button>
        </form>
      </div>

      <ResultPanel result={consultaResult} onClear={() => setConsultaResult(null)} />

      <DomiciliacionManager setToast={setToast} cobradorDefault={DOM_COBRADOR} />
    </div>
  );
}


function HistoryPanel({ refreshKey, setToast }) {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tk = localStorage.getItem('token');
      const url = `${BACKEND_URL}/api/super-admin/sandbox/tests${filter ? `?type=${filter}` : ''}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${tk}` } });
      setTests(res.data || []);
    } catch (err) {
      setToast?.({ type: 'error', message: err.response?.data?.detail || 'Error cargando historial' });
    } finally {
      setLoading(false);
    }
  }, [filter, setToast]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const remove = async (test_id) => {
    if (!window.confirm('¿Eliminar este registro de prueba?')) return;
    try {
      const tk = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/super-admin/sandbox/tests/${test_id}`, { headers: { Authorization: `Bearer ${tk}` } });
      setToast?.({ type: 'success', message: 'Registro eliminado' });
      load();
    } catch (err) {
      setToast?.({ type: 'error', message: err.response?.data?.detail || 'Error al eliminar' });
    }
  };

  return (
    <div className="bg-white rounded-lg border" data-testid="sandbox-history">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <h3 className="font-semibold" style={{ color: '#051C2C' }}>Historial de pruebas</h3>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="text-xs px-2 py-1 border rounded" data-testid="history-filter">
            <option value="">Todas</option>
            <option value="p2p">P2P</option>
            <option value="debito_inmediato_token">DI · Token</option>
            <option value="debito_inmediato_cobro">DI · Cobro</option>
            <option value="domiciliacion_cobro">Dom · Cobro</option>
            <option value="domiciliacion_consulta">Dom · Consulta</option>
          </select>
          <button onClick={load} className="text-xs px-2 py-1 border rounded hover:bg-slate-50 inline-flex items-center gap-1" data-testid="history-refresh">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refrescar
          </button>
        </div>
      </div>
      {tests.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">Aún no se han ejecutado pruebas.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-left">Cód. banco</th>
                <th className="px-3 py-2 text-left">Descripción</th>
                <th className="px-3 py-2 text-left">Referencia</th>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tests.map(t => (
                <Fragment key={t.test_id}>
                  <tr className="hover:bg-slate-50" data-testid={`history-row-${t.test_id}`}>
                    <td className="px-3 py-2 font-mono text-xs">{t.type}</td>
                    <td className="px-3 py-2"><StatusBadge success={t.success} code={t.bank_code} /></td>
                    <td className="px-3 py-2 font-mono text-xs">{t.bank_code || '—'}</td>
                    <td className="px-3 py-2 text-xs max-w-xs truncate" title={t.bank_description || ''}>{t.bank_description || t.error_msg || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{t.bank_reference || '—'}</td>
                    <td className="px-3 py-2 text-xs">{fmtDate(t.created_at)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => setExpanded(expanded === t.test_id ? null : t.test_id)} className="text-xs px-2 py-1 border rounded hover:bg-slate-50 mr-1" data-testid={`history-expand-${t.test_id}`}>
                        {expanded === t.test_id ? 'Ocultar' : 'JSON'}
                      </button>
                      <button onClick={() => remove(t.test_id)} className="text-xs px-2 py-1 border border-red-200 text-red-700 rounded hover:bg-red-50" data-testid={`history-delete-${t.test_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                  {expanded === t.test_id && (
                    <tr className="bg-slate-900">
                      <td colSpan={7} className="px-3 py-3">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 text-[11px]">
                          <div>
                            <div className="text-green-300 font-semibold mb-1">Request</div>
                            <pre className="text-green-200 overflow-x-auto">{JSON.stringify(t.request_payload || {}, null, 2)}</pre>
                          </div>
                          <div>
                            <div className="text-blue-300 font-semibold mb-1">Response</div>
                            <pre className="text-blue-200 overflow-x-auto">{JSON.stringify(t.bank_response || {}, null, 2)}</pre>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


export default function SandboxTab({ setToast }) {
  const [activeMethod, setActiveMethod] = useState('p2p');
  const [refreshKey, setRefreshKey] = useState(0);

  return (
    <div data-testid="sandbox-tab">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#051C2C' }}>Sandbox de Pruebas</h2>
          <p className="text-sm text-slate-600 mt-1">Prueba pagos P2P y Débitos Inmediatos hacia la cuenta operativa de Caura. Las pruebas se aíslan con <code>is_test=true</code> y no afectan wallets ni reportes.</p>
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-hidden mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveMethod('p2p')}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${activeMethod === 'p2p' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            data-testid="method-tab-p2p"
          >
            <Phone className="w-4 h-4 inline mr-2" /> Pago Móvil (P2P)
          </button>
          <button
            onClick={() => setActiveMethod('di')}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${activeMethod === 'di' ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            data-testid="method-tab-di"
          >
            <CreditCard className="w-4 h-4 inline mr-2" /> Débito Inmediato
          </button>
          <button
            onClick={() => setActiveMethod('dom')}
            className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px ${activeMethod === 'dom' ? 'border-purple-600 text-purple-700 bg-purple-50' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
            data-testid="method-tab-dom"
          >
            <Repeat className="w-4 h-4 inline mr-2" /> Domiciliación
          </button>
        </div>
        <div className="p-5">
          {activeMethod === 'p2p' && <P2PSandbox setToast={setToast} onTested={() => setRefreshKey(k => k + 1)} />}
          {activeMethod === 'di' && <DebitoInmediatoSandbox setToast={setToast} onTested={() => setRefreshKey(k => k + 1)} />}
          {activeMethod === 'dom' && <DomiciliacionSandbox setToast={setToast} onTested={() => setRefreshKey(k => k + 1)} />}
        </div>
      </div>

      <div className="mb-6">
        <DIAttemptsAudit setToast={setToast} />
      </div>

      <HistoryPanel refreshKey={refreshKey} setToast={setToast} />
    </div>
  );
}
